'use client';

import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';

import { Navigation } from 'swiper/modules';
import CWSectionHeader from '@/context/global/components/g02/g02-d01/g02-d01-g002-cw-section-header';
import { ContentShelf, ShelfItem } from '@/context/global/types/homeShelf';
import IconMicrophone from '@/context/global/components/Icon/IconMicrophone';
import IconFavorite from '@/context/global/components/Icon/IconFavorite';
import IconCheckbox from '@/context/global/components/Icon/IconCheckbox';
import CWImage from '@/context/global/components/g00/g00-d00-g003-cw-image-fallback';
import IconArrowThin from '@/context/global/components/Icon/IconArrowThin';
import { useKaraokeStore } from '@/context/global/stores/karaokeStore';
import IconLock from '@/context/global/components/Icon/IconLock/IconLock';
import { useAuthStore } from '@/context/global/stores/authStore';
import { redirect, RedirectType } from 'next/navigation';
import CWModalAdvert from '@/context/global/components/g00/g00-d00-g017-cw-modal-advert';
import { useGetOAuth } from '@/context/global/hooks/auth/useGetOAuth';
import { useTranslations } from 'next-intl';

export type CWSongCoversSectionProps = {
  shelf: ContentShelf;
  mode?: 'default' | 'edit';
  interactive?: boolean;
  isViewAll?: boolean;
  onClickViewAll?: () => void;
  onClickPlaySong?: (song: ShelfItem) => void;
  selectedIds?: Set<string | number>;
  onSelectionChange?: (selectedIds: Set<string | number>) => void;
  layout?: 'default' | 'mobile';
  noBackground?: boolean;
  maxCols?: 6 | 7;
  showFavoriteIcon?: boolean;
};

export default function CWSongCoversSection({
  shelf,
  mode = 'default',
  interactive = true,
  onClickPlaySong,
  onSelectionChange,
  selectedIds: externalSelectedIds,
  layout = 'default',
  noBackground = false,
  maxCols = 7,
  showFavoriteIcon = false,
}: CWSongCoversSectionProps) {
  const { user, token } = useAuthStore();
  const t = useTranslations('Common');
  const getOAuth = useGetOAuth();
  const { setSelectedSong } = useKaraokeStore();
  const [isAdvertModalOpen, setIsAdvertModalOpen] = useState(false);
  const selectedId = externalSelectedIds ?? new Set<string | number>();

  const toggleSelect = (id: string | number) => () => {
    if (mode !== 'edit') return;
    const newSet = new Set(selectedId);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    onSelectionChange?.(newSet);
  };

  const redirectViewAll = async () => {
    if (!token) {
      const link = await getOAuth.fetchOAuthUrl();

      if (!link) {
        console.error('Failed to get OAuth URL');
        return;
      }
      setSelectedSong(null);
      redirect(link, RedirectType.push);
    } else {
      redirect(shelf.viewMore.contentUrl, RedirectType.push);
    }
  };
  const renderCard = (data: ShelfItem, i: number) => {
    const isLocked = data.conditions == 2 && user?.badge == 3;

    const handleCardAction = () => {
      if (isLocked && mode !== 'edit') {
        setIsAdvertModalOpen(true);
        return;
      }
      if (mode === 'edit') {
        toggleSelect(data.id ?? i)();
      } else {
        onClickPlaySong ? onClickPlaySong(data) : setSelectedSong(data);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleCardAction();
      }
    };

    return (
      <div
        key={`${data.id}-${i}`}
        role="button"
        tabIndex={isLocked ? -1 : 0}
        aria-disabled={isLocked}
        aria-label={`${data.title || 'Song'} - ${data.artist || 'Artist'}`}
        className={`relative flex h-full w-full flex-shrink-0 outline-none ${
          isLocked && mode !== 'edit' ? 'cursor-not-allowed' : 'cursor-pointer'
        } focus-visible:ring-2 focus-visible:ring-ais-green focus-visible:ring-offset-2 focus-visible:ring-offset-black`}
        onClick={handleCardAction}
        onKeyDown={handleKeyDown}
      >
        {layout === 'default' ? (
          // === Vertical Card Style ===
          <div
            className={`relative flex h-auto w-full flex-col justify-between rounded-[19px] ${noBackground ? '' : 'bg-[rgba(94,94,94,0.2)]'} p-2.5 transition-all duration-200 hover:ring-2 hover:ring-white`}
          >
            <div className="flex flex-col gap-3">
              {/* Image Container */}
              <div className="relative w-full">
                <div className="aspect-square w-full overflow-hidden rounded-[12px]">
                  <CWImage
                    src={data.coverUrl}
                    alt={data.title || 'cover'}
                    width={300} // เพิ่มขนาด source เล็กน้อยเพื่อความคมชัด
                    height={300}
                    className={`h-full w-full object-cover transition-opacity ${
                      selectedId.has(data.id ?? i) && mode == 'edit'
                        ? 'opacity-50'
                        : 'opacity-100'
                    } `}
                  />
                </div>

                {!interactive && mode === 'edit' && selectedId.has(data.id ?? i) && (
                  <div className="absolute right-1 top-1 z-10 flex items-center justify-center rounded-full bg-black/20 p-1 backdrop-blur-sm">
                    <IconCheckbox className="h-6 w-6 text-[#B2D234]" />
                  </div>
                )}

                {!interactive && showFavoriteIcon && data.IsfavoriteSong && mode !== 'edit' && (
                  <div className="absolute bottom-2 right-2">
                    <IconFavorite className="text-[#FF0000]" />
                  </div>
                )}
              </div>

              {/* Text Container */}
              <div className="flex w-full flex-col gap-1">
                {/* Title row */}
                <div className="flex w-full items-center justify-between gap-2">
                  <p
                    className="truncate text-[16px] font-bold text-white md:text-[22px] lg:text-[26px]"
                    title={data.title || `Song Title ${i + 1}`}
                  >
                    {data.title || `Song Title ${i + 1}`}
                  </p>

                  {isLocked && (
                    <div className="flex shrink-0 items-center rounded-full bg-[rgba(20,32,51,0.8)] px-1.5 py-1">
                      <IconLock className="h-3 w-3 text-ais-green sm:h-[17px] sm:w-[14px]" />
                    </div>
                  )}
                </div>

                {/* Artist and mic stats row */}
                <div className="flex w-full items-center justify-between">
                  <p
                    className="truncate text-[14px] font-medium text-[#B5B5B5] md:text-[18px] lg:text-[20px]"
                    title={data.artist || 'Artist Name'}
                  >
                    {data.artist || 'Artist Name'}
                  </p>

                  <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
                    <IconMicrophone className="h-3 w-3 text-[#B5B5B5] sm:h-4 sm:w-3.5" />
                    <p className="text-[14px] font-medium text-[#B5B5B5] md:text-[18px] lg:text-[20px]">
                      {data.numOfListener || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // === Mobile / Horizontal List Style ===
          <div className="relative h-auto w-full rounded-[19px] py-0.5 transition-all duration-200 hover:ring-2 hover:ring-white sm:p-2.5 md:bg-[#222222cc]">
            <div className="flex w-full flex-row items-center gap-3 sm:gap-4 md:flex-col md:gap-2">
              {/* Image Container */}
              <div className="relative aspect-square w-16 shrink-0 sm:w-20 md:w-full">
                <div className="h-full w-full overflow-hidden rounded-[12px]">
                  <CWImage
                    src={data.coverUrl}
                    alt={data.title || 'cover'}
                    width={200}
                    height={200}
                    className={`h-full w-full object-cover ${
                      selectedId.has(data.id ?? i) ? 'opacity-50' : 'opacity-100'
                    } `}
                  />
                </div>
                {/* Icons... (Mobile Layout) */}
                {!interactive && mode === 'edit' && selectedId.has(data.id ?? i) && (
                  <div className="absolute right-1 top-1 flex items-center justify-center">
                    <IconCheckbox className="h-4 w-4 text-[#B2D234] md:h-6 md:w-6" />
                  </div>
                )}
              </div>

              {/* Text Container - Added min-w-0 for truncate to work in flex */}
              <div className="flex min-w-0 flex-1 flex-col justify-center md:w-full">
                <div className="flex w-full items-center justify-between gap-2">
                  <p
                    className="truncate text-[16px] font-bold text-white md:text-[22px] lg:text-[26px]"
                    title={data.title || `Song Title ${i + 1}`}
                  >
                    {data.title || `Song Title ${i + 1}`}
                  </p>
                  {isLocked && (
                    <div className="flex size-[26px] shrink-0 items-center justify-center rounded-full bg-[rgba(20,32,51,0.8)]">
                      <IconLock className="size-4 text-ais-green" />
                    </div>
                  )}
                </div>

                <div className="mt-1 flex w-full items-center justify-between md:mt-0">
                  <p
                    className="truncate text-[14px] font-medium text-[#B5B5B5] md:text-[18px] lg:text-[20px]"
                    title={data.artist || 'Artist Name'}
                  >
                    {data.artist || 'Artist Name'}
                  </p>

                  <div className="hidden items-center gap-1.5 md:flex">
                    <IconMicrophone className="h-3 w-3 text-[#B5B5B5]" />
                    <p className="text-[14px] text-[#B5B5B5] md:text-[18px] lg:text-[20px]">
                      {data.numOfListener || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const items = Array.isArray(shelf.items) ? shelf.items : [];

  return (
    <section className="h-auto w-full py-4">
      <div className="mb-2 flex w-full items-center justify-between">
        <CWSectionHeader title={shelf.title} />
        {shelf.viewMore.enabled && shelf.viewMore.contentUrl.length !== 0 && (
          <button
            className="group flex min-w-[100px] cursor-pointer items-center justify-end gap-1 text-base font-bold text-white transition-all duration-300 hover:text-ais-green sm:text-lg md:text-xl"
            onClick={redirectViewAll}
          >
            {t('viewAll')}
            <IconArrowThin className="rotate-180 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        )}
      </div>

      {interactive ? (
        <div className="relative h-auto w-full">
          <Swiper
            className="!pt-2" // เพิ่ม padding กันเงาตกกระทบถูกตัด
            slidesPerView={1.3}
            spaceBetween={12}
            slidesOffsetBefore={5} // ระยะห่างจากขอบซ้ายสำหรับ Mobile
            slidesOffsetAfter={5}
            navigation={true}
            modules={[Navigation]}
            breakpoints={{
              340: { slidesPerView: 1.5, spaceBetween: 12 },
              480: { slidesPerView: 2.2, spaceBetween: 12 },
              640: {
                slidesPerView: 2.5,
                spaceBetween: 12,
                slidesOffsetBefore: 0,
                slidesOffsetAfter: 0,
              },
              768: {
                slidesPerView: 3.2,
                spaceBetween: 12,
              },
              1024: { slidesPerView: 4.2, spaceBetween: 12 },
              1280: { slidesPerView: 5.2, spaceBetween: 12 },
              1536: { slidesPerView: 6.2 },
            }}
          >
            {items.map((data, i) => (
              <SwiperSlide
                key={`${data.id ?? `fallback-${i}`}`}
                className="h-auto !w-[140px] md:!w-[220px]"
              >
                {renderCard(data, i)}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      ) : (
        <>
          {layout === 'default' ? (
            <div className={`grid grid-cols-2 gap-3 px-0 sm:grid-cols-3 sm:gap-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 ${maxCols === 7 ? '2xl:grid-cols-7' : ''}`}>
              {items.map((data, i) => renderCard(data, i))}
            </div>
          ) : (
            <div className={`grid grid-cols-1 gap-3 px-0 sm:grid-cols-3 sm:gap-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 ${maxCols === 7 ? '2xl:grid-cols-7' : ''}`}>
              {items.map((data, i) => renderCard(data, i))}
            </div>
          )}
        </>
      )}

      {/* Modal Advert for locked songs */}
      {isAdvertModalOpen && (
        <CWModalAdvert
          isOpen={isAdvertModalOpen}
          onClose={() => setIsAdvertModalOpen(false)}
        />
      )}
    </section>
  );
}
